import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { insufficientReasonCopy } from '../utils/insufficientSchedule';

const STRINGS = appStrings.modules.satActBooster.insufficientSchedule;

type InsufficientScheduleCalloutProps = Readonly<{
  /** Reason codes that raised the warning; unknown codes render a generic line. */
  reasons: readonly string[];
  id?: string;
}>;

/**
 * A non-blocking warning callout listing why a study schedule looks light. Renders nothing when
 * there are no reasons, so callers can pass an empty list without guarding.
 */
export const InsufficientScheduleCallout = ({
  reasons,
  id = 'booster-insufficient-schedule-callout',
}: InsufficientScheduleCalloutProps) => {
  if (reasons.length === 0) return null;

  return (
    <Box id={id} role="status" sx={containerSx}>
      <AppIcon
        iconName="AlertTriangle"
        diameter="20px"
        color="Colors/Foreground/fg-warning-secondary"
      />
      <Box sx={textColumnSx}>
        <Typography variant="text-sm-semibold" sx={titleSx}>
          {STRINGS.calloutTitle}
        </Typography>
        {reasons.map((reason) => (
          <Typography key={reason} variant="text-sm-regular" sx={reasonSx}>
            {insufficientReasonCopy(reason)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-warning-primary'],
  borderRadius: backrs.radius['radius-lg'],
});

const textColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-warning-primary'],
});

const reasonSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
